import { ComplianceAppView } from '../view/ComplianceAppView';
import { templateComplianceApp } from '../view/template';
import { ComplianceAppRepository } from '../repository/ComplianceAppRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { ComplianceAppWebComponent } from '..';
import { ColumnKeys, ComplianceAppWebComponentSchema } from '../types';
import {
  getSideNavigationPanelSmartViewsListener,
  getSmartViewsListeners,
} from 'apps/webcomponents/src/grid/genericGridSmartViews/utils';
import { GridEvents } from 'apps/webcomponents/src/grid/types/common';
import { ComplianceSideMenuWebComponent } from '../../complianceSideMenu/component/ComplianceSideMenuWebComponent';
import { ComplianceEvents } from '../../common/types';
import { GzDialogWebComponent, GzToastNotificationWebComponent } from 'apps/webcomponents/src/index.ui';
import { GzDialog } from 'apps/webcomponents/src/ui/gzDialog/component/GzDialog';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { ComplianceDownloadReportFormWebComponent } from '../../complianceDownloadReportForm';
import { downloadBlob } from 'apps/webcomponents/src/utils/files.utils';
import { GzToast, ToastSeverity } from 'apps/webcomponents/src/ui/gzToastNotification/type';
import {
  ComplianceStandard,
  ControlsOverview,
  ComplianceStandardTranslations,
  ComplianceStandards,
  COMPLIANCE_LIMIT,
} from '@nirvana/shared/dtos/types/compliance-reports/gz-console';
import { handleExactMatchCustomValues } from '../../../grid/utils/filters';
import { ColumnKeys as MisconfigurationsColumnKeys } from '../../../riskManagement/misconfigurationsApp/types';
import { ColumnKeys as UserBehaviorRisksColumnKeys } from '../../../riskManagement/userBehaviorRisksApp/types';
import { ColumnKeys as DevicesGridColumnKeys } from '../../../riskManagement/devicesApp/types';
import { ColumnKeys as UsersColumnKeys } from '../../../riskManagement/usersApp/types';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { complianceStandardOptions, getComplianceLearnMoreButton } from '../../common/util';
import { ComplianceDisclaimer } from '../../common/constant';
import { Categories } from 'apps/webcomponents/src/grid/genericGridSmartViews/types';
import { ElementClass } from 'apps/webcomponents/src/utils/htmlElement.utils';

export class ComplianceAppController extends BaseWebComponentController {
  public component: ComplianceAppWebComponent;
  public dataRepository: ComplianceAppRepository;
  public view: ComplianceAppView;
  public hasAccessToComplianceStandard: boolean = false;
  public viewConstructor: typeof ComplianceAppView;
  public dataRepositoryConstructor: typeof ComplianceAppRepository;

  constructor(component: ComplianceAppWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: ComplianceAppView,
      viewTemplate: templateComplianceApp,
      dataRepositoryConstructor: ComplianceAppRepository,
    });
  }

  public computeEventListeners() {
    return [
      ...getSmartViewsListeners(
        this.view.dom.$grid,
        this.view.dom.$gridSmartViews,
        this.view.buildGrid.bind(this.view),
        this.view.getTranslations()
      ),
      ...getSideNavigationPanelSmartViewsListener(this.view.dom.$pushBodyLayout, this.view.dom.$gridSmartViews),
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROW_CLICKED,
        callback: this.handleRowClicked,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROWS_LOADING_FINISHED,
        callback: ({ detail }) => this.handleRowsLoadingFinishedEvent(detail),
      },
      {
        element: this.view.dom.$grid,
        event: ComplianceEvents.COMPLIANCE_GRID_DATA_LOADED,
        callback: this.handleFiltersUpdatedEvent,
      },
      {
        element: this.view.dom.$grid,
        event: ComplianceEvents.DOWNLOAD_REPORT,
        callback: this.handleDownloadReportEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: ComplianceEvents.SIDE_MENU_RISKS_DETAILS,
        callback: this.handleSideMenuRisksDetailsEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: ComplianceEvents.SIDE_MENU_ASSETS_DETAILS,
        callback: this.handleSideMenuAssetsDetailsEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: ComplianceEvents.VIEW_MISCONFIGURATION_DETAILS,
        callback: this.handleViewMisconfigurationDetailsEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: ComplianceEvents.VIEW_HUMAN_RISKS_BY_RISKS,
        callback: this.handleViewHumanRisksByRisksEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: ComplianceEvents.VIEW_RESOURCES_BY_MISCONFIGURATIONS,
        callback: this.handleViewResourcesByMisconfigurations,
      },
      {
        element: this.view.dom.$sideMenu,
        event: ComplianceEvents.VIEW_IDENTITIES_BY_RISKS,
        callback: this.handleViewIdentitiesByRisksEvent,
      },
    ];
  }

  async getDataForView(): Promise<ComplianceAppWebComponentSchema> {
    return this.dataRepository.getDataForView();
  }

  async handleRowClicked(event: CustomEvent<{ rowDetails: ControlsOverview }>) {
    if (!event.detail.rowDetails) {
      // header was clicked, so we do nothing
      return;
    }

    const component = new ComplianceSideMenuWebComponent();

    const companyId = this.component.currentCompanyId;
    const complianceStandard = this.component.currentComplianceStandard as ComplianceStandard;

    const misconfigurationRisksCount = event.detail.rowDetails.findings.misconfiguration.indicatorIds?.length ?? 0;
    const userBehaviorRisksCount = event.detail.rowDetails.findings.userBehaviorRisk.indicatorIds?.length ?? 0;

    component.companyId = companyId;
    component.complianceLearnMoreButton = getComplianceLearnMoreButton();
    component.hasAccessToComplianceStandard = this.hasAccessToComplianceStandard;
    component.complianceData = {
      controlName: event.detail.rowDetails.controlDetails.name,
      controlDescription: event.detail.rowDetails.controlDetails.description,
      controlId: event.detail.rowDetails.controlDetails.id,
      complianceStandardLabel: ComplianceStandardTranslations[complianceStandard] ?? complianceStandard,
      complianceStandard: complianceStandard,
      sectionName: event.detail.rowDetails.sectionDetails.name,
      sectionId: event.detail.rowDetails.sectionDetails.id,
      categoryLabel: event.detail.rowDetails.category,

      // drawer Risks & Assets items
      misconfigurationRisks: [],
      userBehaviorRisks: [],
      resourceAssets: [],
      identityAssets: [],

      misconfigurationRisksTotalCount: misconfigurationRisksCount,
      userBehaviorRisksTotalCount: userBehaviorRisksCount,
      misconfigurationRisksIndicatorIds: event.detail.rowDetails.findings.misconfiguration.indicatorIds ?? [],
      userBehaviorRiskIndicatorIds: event.detail.rowDetails.findings.userBehaviorRisk.indicatorIds ?? [],

      complianceScore: event.detail.rowDetails.controlDetails.checksStats.passedPercentage as number,
      checksBreakdown: event.detail.rowDetails.controlDetails.checksStats.checksResults,

      totalChecks: event.detail.rowDetails.controlDetails.checksStats.totalChecks,
    };

    this.view.dom.$sideMenu.setMenuContent(component);
    this.view.dom.$sideMenu.openSideMenu();
  }

  private disableDownloadReportButton(value: boolean) {
    this.view.dom.downloadReport.$button.disabled = value;
  }

  private getComplianceStandardFilter(filters: any) {
    const complianceStandardFilter = filters.find(filter => !!filter[ColumnKeys.COMPLIANCE_STANDARD]);
    const complianceStandardFilterValue = complianceStandardFilter
      ? complianceStandardFilter[ColumnKeys.COMPLIANCE_STANDARD]?.value ?? null
      : null;

    return {
      complianceStandardValue: complianceStandardFilterValue,
      hasAccessToComplianceStandard: ComplianceStandards[complianceStandardFilterValue].free ?? false,
    };
  }

  private async handleFiltersUpdatedEvent(ev: WebComponentEvent) {
    const newFilters = ev.detail.filters || [];
    const overViewData = ev.detail.data || null;

    const companyFilter = newFilters.find(filter => !!filter[ColumnKeys.COMPANY]);
    if (companyFilter && companyFilter[ColumnKeys.COMPANY]) {
      // `companyFilter` can have the special value `myCompany`
      handleExactMatchCustomValues(companyFilter[ColumnKeys.COMPANY]);
    }
    const companyFilterValue = companyFilter ? companyFilter[ColumnKeys.COMPANY]?.exactMatch ?? null : null;

    const { complianceStandardValue, hasAccessToComplianceStandard } = this.getComplianceStandardFilter(newFilters);

    // TRUE if selected compliance standard is free or if the user has access to premium compliance standard
    this.hasAccessToComplianceStandard = overViewData.features['compliancePremiumUsable'] || hasAccessToComplianceStandard;

    if (companyFilterValue !== this.component.currentCompanyId) {
      // Only allow downloading of reports when there are rows that were loaded for the selected company
      this.disableDownloadReportButton(true);
    }

    this.component.currentCompanyId = companyFilterValue;
    this.component.currentComplianceStandard = complianceStandardValue;

    this.view.setComplianceOverview({
      overallCompliance: overViewData.checksStats.overAllStats.passedPercentage,
      compliantCount: overViewData.checksStats.overAllStats.checksResults.compliant,
      nonCompliantCount: overViewData.checksStats.overAllStats.checksResults.nonCompliant,
      ignoredCount: overViewData.checksStats.overAllStats.checksResults.ignored,
      totalChecks: overViewData.checksStats.overAllStats.totalChecks,
    });
  }

  private handleViewMisconfigurationDetailsEvent(event: CustomEvent) {
    const processedIndicatorIds = event.detail.indicatorIds.map(indicatorId => {
      return {
        value: indicatorId,
      };
    });

    const params: {
      filters: Partial<Record<MisconfigurationsColumnKeys, unknown>>[];
      openSideMenuForFirstItem: boolean;
    } = {
      filters: [
        {
          [MisconfigurationsColumnKeys.MISCONFIGURATION_NAME]: {
            complexEnumFilter: processedIndicatorIds,
          },
        },
        {
          [MisconfigurationsColumnKeys.COMPANY]: { exactMatch: event.detail.companyId },
        },
      ],
      openSideMenuForFirstItem: processedIndicatorIds.length === 1,
    };

    NavigationHelper.navigateTo('/risk-management-findings', params);
  }

  private async handleViewHumanRisksByRisksEvent(event: CustomEvent) {
    const processedIndicatorIds = event.detail.indicatorIds.map(indicatorId => {
      return {
        value: indicatorId,
      };
    });

    const params: {
      filters: Partial<Record<MisconfigurationsColumnKeys, unknown>>[];
      openSideMenuForFirstItem: boolean;
    } = {
      filters: [
        {
          [UserBehaviorRisksColumnKeys.USER_BEHAVIOR_RISK]: {
            complexEnumFilter: processedIndicatorIds,
          },
        },
        {
          [UserBehaviorRisksColumnKeys.COMPANY]: {
            exactMatch: event.detail.companyId,
          },
        },
      ],
      openSideMenuForFirstItem: processedIndicatorIds.length === 1,
    };

    NavigationHelper.navigateTo('/risk-management-identity-risks', params);
  }

  private handleViewResourcesByMisconfigurations(event: CustomEvent) {
    const operatingSystemFilter = event.detail.operatingSystem;

    // `View all` params
    const params: { filters: Partial<Record<DevicesGridColumnKeys, unknown>>[]; openSideMenuForFirstItem?: boolean } = {
      filters: [
        {
          [DevicesGridColumnKeys.COMPANY]: { exactMatch: event.detail.companyId },
        },
      ],
    };

    if (operatingSystemFilter != null) {
      params.filters.push({
        [DevicesGridColumnKeys.OS]: { in: operatingSystemFilter },
      });
    }

    // Selecting specific `endpoint` additional params
    if (event.detail.assetIds) {
      params.filters.push({
        [DevicesGridColumnKeys.DEVICE_NAME]: {
          complexEnumFilter: event.detail.assetIds,
        },
      });
      params.openSideMenuForFirstItem = true;
    }

    NavigationHelper.navigateTo('/risk-management-resources', params);
  }

  private handleViewIdentitiesByRisksEvent(event: CustomEvent) {
    const userDevices = event.detail.applicableResource;

    // Base params
    const params: { filters: Partial<Record<UsersColumnKeys, unknown>>[]; openSideMenuForFirstItem?: boolean } = {
      filters: [
        {
          [UsersColumnKeys.COMPANY]: { exactMatch: event.detail.companyId },
        },
      ],
    };

    if (userDevices?.length > 0) {
      params.filters.push({
        [UsersColumnKeys.USER_DEVICES]: {
          complexEnumFilter: event.detail.applicableResource.map(resource => {
            return { label: resource.endpointName, value: resource.endpointId };
          }),
        },
      });
    }

    // Selecting specific `identity` additional params
    if (event.detail.assetIds) {
      params.filters.push({
        [UsersColumnKeys.USERNAME]: {
          complexEnumFilter: event.detail.assetIds,
        },
      });
      params.openSideMenuForFirstItem = true;
    }

    NavigationHelper.navigateTo('/risk-management-identities', params);
  }

  private handleSideMenuRisksDetailsEvent(event: CustomEvent) {
    if (!this.hasAccessToComplianceStandard) {
      return;
    }

    const component: ComplianceSideMenuWebComponent = event.detail.component;
    const routineTranslations = Application.modules.riskManagement.controller.helpers.routinesTranslations;
    const basePayload = {
      companyId: event.detail.companyId,
      complianceStandard: event.detail.complianceStandard,
    };

    if (
      component.complianceData.misconfigurationRisksTotalCount &&
      component.complianceData.misconfigurationRisksTotalCount > 0 &&
      component.complianceData.misconfigurationRisks != null &&
      component.complianceData.misconfigurationRisks.length === 0
    ) {
      component.misconfigurationRisksLoading = true;

      this.dataRepository
        .getMisconfigurations({
          ...basePayload,
          filters: {
            indicatorId: {
              in: event.detail.indicatorIds.misconfiguration,
            },
          },
        })
        .then(res => {
          component.complianceData.misconfigurationRisks =
            res.data?.map(x => {
              return {
                name: routineTranslations.translateRoutineName(x.indicatorId),
                indicatorId: x.indicatorId,
                checksStats: x.checksStats,
                riskScore: x.riskScore,
                state: x.state,
              };
            }) ?? [];

          // Some risks from compliance mapping json are not available for some companies
          component.complianceData.misconfigurationRisksTotalCount = res.total;

          component.misconfigurationRisksLoading = false;
        });
    }

    if (
      component.complianceData.userBehaviorRisksTotalCount &&
      component.complianceData.userBehaviorRisksTotalCount > 0 &&
      component.complianceData.userBehaviorRisks != null &&
      component.complianceData.userBehaviorRisks.length === 0
    ) {
      component.userBehaviorRisksLoading = true;

      this.dataRepository
        .getUserBehaviorRisks({
          ...basePayload,
          filters: {
            indicatorId: {
              in: event.detail.indicatorIds.userBehaviorRisk,
            },
          },
        })
        .then(res => {
          component.complianceData.userBehaviorRisks =
            res.data?.map(x => {
              return {
                name: routineTranslations.translateRoutineName(x.indicatorId),
                indicatorId: x.indicatorId,
                checksStats: x.checksStats,
                riskScore: x.riskScore,
                state: x.state,
              };
            }) ?? [];

          // Some risks from compliance mapping json are not available for some companies
          component.complianceData.userBehaviorRisksTotalCount = res.total;

          component.userBehaviorRisksLoading = false;
        });
    }
  }

  private handleSideMenuAssetsDetailsEvent(event: CustomEvent) {
    if (!this.hasAccessToComplianceStandard) {
      return;
    }
    const component: ComplianceSideMenuWebComponent = event.detail.component;
    const basePayload = {
      companyId: event.detail.companyId,
      complianceStandard: event.detail.complianceStandard,
    };

    if (
      component.complianceData.misconfigurationRisksTotalCount &&
      component.complianceData.misconfigurationRisksTotalCount > 0 &&
      component.complianceData.resourceAssets != null &&
      component.complianceData.resourceAssets.length === 0
    ) {
      // If misconfigurationRisks > 0, we will query for control resources using the indicatorIds.
      component.resourceAssetsLoading = true;

      this.dataRepository
        .getControlResources({
          ...basePayload,
          filters: {
            indicatorId: {
              in: event.detail.indicatorIds.misconfiguration,
            },
          },
        })
        .then(res => {
          component.complianceData.resourceAssets =
            res.data?.map(x => {
              return {
                name: x.endpointName,
                id: x.endpointId,
                checksStats: x.checksStats,
                state: x.state,
                riskScore: x.scoreBreakdown.finalScore,
              };
            }) ?? [];
          component.complianceData.resourceAssetsTotalCount = res.total;
          component.complianceData.controlExtraDetails = {
            ...component.complianceData?.controlExtraDetails,
            controlOperatingSystem: res.extraDetails?.control?.operatingSystem,
          };

          component.resourceAssetsLoading = false;
        });
    }

    if (
      component.complianceData.userBehaviorRisksTotalCount &&
      component.complianceData.userBehaviorRisksTotalCount > 0 &&
      component.complianceData.identityAssets != null &&
      component.complianceData.identityAssets.length === 0
    ) {
      component.identityAssetsLoading = true;

      this.dataRepository
        .getControlIdentities({
          ...basePayload,
          filters: {
            indicatorId: {
              in: event.detail.indicatorIds.userBehaviorRisk,
            },
          },
        })
        .then(res => {
          component.complianceData.identityAssets =
            res.data?.map(x => {
              return {
                name: x.username,
                id: x.username, // Filter uses username as well, instead of the uniqueIdentifier
                checksStats: x.checksStats,
                state: x.state,
                riskScore: x.scoreBreakdown.finalScore,
              };
            }) ?? [];
          component.complianceData.identityAssetsTotalCount = res.total;
          component.complianceData.controlExtraDetails = {
            ...component.complianceData?.controlExtraDetails,
            controlApplicableResource: res.extraDetails?.control?.applicableResource,
          };

          component.identityAssetsLoading = false;
        });
    }
  }

  private async handleDownloadReportEvent(event: CustomEvent): Promise<void> {
    const formComponent = new ComplianceDownloadReportFormWebComponent();
    formComponent.selectedComplianceStandardFilter = this.component.currentComplianceStandard;

    formComponent.complianceStandardOptions = complianceStandardOptions.filter(
      complianceStandard => this.view.compliancePremiumUsable || complianceStandard.free
    );

    formComponent.onSubmit = async ({
      complianceStandard,
      format,
    }: {
      complianceStandard: ComplianceStandard;
      format: string;
    }) => {
      // do not download if compliance standard is not free and not on premium tier
      if (!this.view.compliancePremiumUsable && !ComplianceStandards[complianceStandard].free) {
        return;
      }

      const companyId = this.component.currentCompanyId;
      if (
        companyId &&
        typeof complianceStandard === 'string' &&
        typeof format === 'string' &&
        Object.values(ComplianceStandard).includes(complianceStandard as ComplianceStandard)
      )
        await this.handleExportReportFile(companyId, complianceStandard as ComplianceStandard, format);
      dialog.close();
    };

    formComponent.onCancel = () => {
      dialog.close();
    };

    const dialogTitle = translateFn('Download report?');
    const dialogConfigs: GzDialog = {
      headline: dialogTitle,
      bodyContent: formComponent,
      isOpen: true,
      isWindow: false,
      closableOutsideBox: false,
    };
    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);
  }

  private async handleExportReportFile(
    companyId: string,
    complianceStandard: ComplianceStandard,
    format: string
  ): Promise<void> {
    let response: any;

    try {
      if (format === 'PDF') {
        response = await this.dataRepository.generatePdf({
          companyId,
          complianceStandard,
        });
      } else if (format === 'XLSX') {
        response = await this.dataRepository.generateXlsx({
          companyId,
          complianceStandard,
        });
      }

      if (response.empty === true) {
        // Show empty error.
      } else {
        const blob = new Blob([response.file], { type: response.mime });

        downloadBlob(blob, response.name);

        const toastNotificationConfigs: GzToast = {
          message: translateFn('Successfully downloaded {fileName}', { fileName: response.name }),
          hasAction: false,
          severity: ToastSeverity.SUCCESS,
          keepOpen: false,
          autoCloseDelayMs: 10000,
        };

        GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
      }
    } catch (error) {
      // Show error toast and handle cleanup.
      const failedToastNotificationConfigs: GzToast = {
        message: translateFn('Failed to download {fileName}', { fileName: response.name }),
        hasAction: false,
        severity: ToastSeverity.ERROR,
        keepOpen: false,
        autoCloseDelayMs: 10000,
      };

      GzToastNotificationWebComponent.buildToastWrapperElement(failedToastNotificationConfigs);
    }
  }

  private handleFreetier(totalEndpoints: number, category: string) {
    this.view.dom.complianceOverview.$content.hasAccessToComplianceStandard = this.hasAccessToComplianceStandard;

    if (totalEndpoints > COMPLIANCE_LIMIT.FREE_TIER_LIMIT) {
      // show free tier limit disclaimer if total endpoints exceed 1000
      this.view.setDisclaimer(ComplianceDisclaimer.FREE_TIER_LIMIT);
    }

    if (!this.hasAccessToComplianceStandard) {
      // display purchase add-on disclaimer if compliance standard is not free
      this.view.setDisclaimer(ComplianceDisclaimer.PURCHASE_ADD_ON);
      // disable smart view action button if compliance standard is not free
      this.view.setDisableSmartViewAction(true);
    }

    // for Saved Smart Views
    if (category === Categories.SAVED && !this.hasAccessToComplianceStandard) {
      // hide info banner
      this.view.setElementClass(this.view.dom.$licenseDisclaimer.$text, ElementClass.HIDDEN);
      // show license key not detected state if smart view is not read-only and compliance standard is not free
      this.view.setNoLicenseKeyDetectedView();
    }

    this.disableDownloadReportButton(!this.hasAccessToComplianceStandard);
  }

  private handlePremiumTier(totalEndpoints: number) {
    if (totalEndpoints > COMPLIANCE_LIMIT.ENDPOINT_COUNT_HARD_LIMIT) {
      // show disclaimer if total endpoints exceed 100000
      this.view.setDisclaimer(ComplianceDisclaimer.HARD_LIMIT);
    }
    this.hasAccessToComplianceStandard = true;
    this.view.dom.complianceOverview.$content.hasAccessToComplianceStandard = true;
    this.view.compliancePremiumUsable = true;

    this.disableDownloadReportButton(false);
  }

  private handleLicenseState() {
    this.view.setDefaultState();
    const summaryExtraDetails = this.view.dom.$grid.controller.getSummaryExtraDetailsForView();
    const { iorUsable, totalEndpoints, compliancePremiumUsable } = summaryExtraDetails.company;
    const { category } = this.view.dom.$gridSmartViews.controller.getActiveView();

    if (iorUsable) {
      if (compliancePremiumUsable) {
        // Premium tier
        this.handlePremiumTier(totalEndpoints);
      } else {
        // Free tier
        this.handleFreetier(totalEndpoints, category);
      }
    }
  }

  private async handleRowsLoadingFinishedEvent(detail: { loadedItems: number }) {
    // Only allow downloading of reports when there are rows that were loaded at least once
    if (detail.loadedItems > 0) {
      this.disableDownloadReportButton(false);
    }

    if (this.view.dom.$grid.dataProvider.isEmptyRowsWithOverallChecks) {
      this.view.setEmptyRowsWithOverallChecks();
    }

    this.view.setDisableFilterScore(this.hasAccessToComplianceStandard);

    this.handleLicenseState();
  }
}
